import { Outlet, Link, useLocation } from 'react-router-dom';
import { PawPrint, Search, Heart, Handshake, User, Menu, X, LogIn } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@rehome/shared';

/**
 * Platform Layout — persistent nav for app.rehome.hu
 * Public routes always visible; protected routes require auth.
 */
const PlatformLayout: React.FC = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  const publicItems = [
    { path: '/animals', label: 'Gazdikeresők', icon: Search },
    { path: '/match', label: 'Smart Match', icon: Handshake },
    { path: '/donate', label: 'Adományozás', icon: Heart },
  ];

  const protectedItems = [
    { path: '/my-animals', label: 'Saját Állataim', icon: PawPrint },
    { path: '/profile', label: 'Profil', icon: User },
  ];

  const navItems = [...publicItems, ...(user ? protectedItems : [])];

  // A "Belépés" gomb is magával viszi, hol állt éppen a felhasználó, így a
  // bejelentkezés után nem a listán, hanem ugyanazon az oldalon köt ki.
  const loginHref = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top navbar */}
      <nav className="fixed w-full z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-teal-600 p-1.5 rounded-lg">
                <PawPrint className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">ReHome</span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.startsWith(item.path)
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-600 hover:text-teal-700 hover:bg-teal-50'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
              {!user && (
                <Link
                  to={loginHref}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 transition-colors ml-2"
                >
                  <LogIn className="w-4 h-4" />
                  Belépés
                </Link>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Menü bezárása' : 'Menü megnyitása'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white">
            <div className="px-4 py-2 space-y-1">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${
                    location.pathname.startsWith(item.path)
                      ? 'bg-teal-50 text-teal-700'
                      : 'text-gray-600'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
              {!user && (
                <Link
                  to={loginHref}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium bg-teal-600 text-white"
                >
                  <LogIn className="w-4 h-4" />
                  Belépés
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default PlatformLayout;
